import { useMemo } from "react";
// React Query
import { useQueryClient } from "@tanstack/react-query";
// MUI
import { Skeleton } from "@mui/material";

// Hooks
import { useSalesLogChart } from "@DataPages/context/salesOperations/salesLog/SalesLogChartsContext";
import { useLocationId } from "@Common/hooks/Location/useLocationId.tsx";
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";

// Helpers
import { getTabSales } from "@DataPages/helpers/salesOperations/salesLog/utils";
import { getSalesOperationName } from "@DataPages/components/SalesOperations/constants";

// Components
import RoundedBox from "@Layouts/components/Basics/RoundedBox";
import TableLoader from "@Layouts/components/Skeletons/TableLoader";
import SalesLogTable from "@DataPages/components/SalesOperations/Tabs/SalesLog/Tables/SalesLogTable";

// Types
import { ICommissionCycle } from "@Common/api/types/CommissionCycle";
import { ISalesLog } from "@DataPages/types/salesOperations/salesLog/model";
import { GraphqlSubscriptionResponse } from "@Common/api/types/GraphqlSubscriptionResponse";

type SalesLogSectionProps = {
	salesLogs: ISalesLog[];
	selectedCommissionCycle: ICommissionCycle;
	isLoading: boolean;
};

const SALES_LOG_SALES_QUERY_KEY = getSalesOperationName("QUERY", "SALES_LOG_SALES");
const SALES_LOG_SALES_SUBSCRIPTION_KEY = getSalesOperationName("SUBSCRIPTION", "SALES_LOG_SALES");
export default function SalesLogTableSection(props: SalesLogSectionProps) {
	const { selectedCommissionCycle, isLoading, salesLogs } = props;

	const queryClient = useQueryClient();
	const { getSubscription } = useSubscriptions();
	const { tabSelected, includedDealType } = useSalesLogChart();
	const locationId = useLocationId();

	const tabSales = useMemo(() => getTabSales(tabSelected, salesLogs, includedDealType), [tabSelected, salesLogs, includedDealType]);

	const SalesLogMemoizedMemoTable = useMemo(
		() => <SalesLogTable data={tabSales} total={salesLogs.length} tableTitle={`${selectedCommissionCycle?.title} Monthly Sales Log`} />,
		[selectedCommissionCycle?.title, tabSales, tabSelected]
	);

	/********************************************* SUBSCRIPTIONS ************************************************/
	const salesLogSubscription = getSubscription([SALES_LOG_SALES_SUBSCRIPTION_KEY, selectedCommissionCycle?._id]);
	salesLogSubscription?.subscriptions?.onUpdateSalesLogSales?.setCallback(
		(result: GraphqlSubscriptionResponse<"onUpdateSalesLogSales", ISalesLog>) => {
			const updatedSalesLog = result.data?.onUpdateSalesLogSales;
			if (updatedSalesLog) {
				queryClient.invalidateQueries({ queryKey: [SALES_LOG_SALES_QUERY_KEY, locationId, selectedCommissionCycle?._id] });
			}
		}
	);

	return (
		<RoundedBox className={"min-h-[630px] !h-[630px]"}>
			{isLoading ? (
				<>
					<Skeleton variant="rectangular" height={25} width={350} className={"rounded-[12px]"} />
					<TableLoader toolbar buttons={3} content rows={5} multiTitles={7} />
				</>
			) : (
				<>{SalesLogMemoizedMemoTable}</>
			)}
		</RoundedBox>
	);
}
